import * as THREE from 'three';
import {
  EDGE_ALIGN_GOOD_DEG,
  EDGE_ALIGN_OK_DEG,
  EDGE_QUALITY,
  HIT_MIN_TIP_SPEED_MPS,
  RAD_TO_DEG,
  THRUST_ALIGN_MIN_COS,
  type EdgeQuality,
} from '../config/constants';

export type { EdgeQuality };

/**
 * Edge alignment angle in radians between the edge direction and the projection
 * of the tip velocity onto the plane perpendicular to the blade axis.
 *
 * 0 = filo perfetto (edge leads the cut).
 * π/2 = piattonata (flat slap).
 *
 * Returns null if inputs are not finite or velocity magnitude is too small to be meaningful.
 */
export function edgeAlignmentAngle(
  tipVelocity: THREE.Vector3,
  bladeAxis: THREE.Vector3,
  edgeDir: THREE.Vector3,
  minSpeedMPerSec: number = HIT_MIN_TIP_SPEED_MPS,
): number | null {
  if (!finite3(tipVelocity) || !finite3(bladeAxis) || !finite3(edgeDir)) return null;
  const speed = tipVelocity.length();
  if (!isFinite(speed) || speed < minSpeedMPerSec) return null;

  const axisN = bladeAxis.clone().normalize();
  const edgeN = edgeDir.clone().normalize();

  const longitudinal = axisN.clone().multiplyScalar(tipVelocity.dot(axisN));
  const perp = tipVelocity.clone().sub(longitudinal);
  const perpLen = perp.length();
  if (perpLen < 1e-4) return null;

  const cosTheta = Math.abs(perp.divideScalar(perpLen).dot(edgeN));
  const clamped = Math.min(1, Math.max(0, cosTheta));
  return Math.acos(clamped);
}

/**
 * True when the tip velocity is mostly along the blade axis (thrust / colpo di punta).
 * Checked before edge alignment, since a pure thrust has negligible perpendicular component.
 */
export function isThrust(
  tipVelocity: THREE.Vector3,
  bladeAxis: THREE.Vector3,
  minSpeedMPerSec: number = HIT_MIN_TIP_SPEED_MPS,
): boolean {
  if (!finite3(tipVelocity) || !finite3(bladeAxis)) return false;
  const speed = tipVelocity.length();
  if (!isFinite(speed) || speed < minSpeedMPerSec) return false;
  const axisN = bladeAxis.clone().normalize();
  const cosAxis = Math.abs(tipVelocity.dot(axisN)) / speed;
  return cosAxis >= THRUST_ALIGN_MIN_COS;
}

export function classifyEdge(angleRad: number | null): EdgeQuality | null {
  if (angleRad === null || !isFinite(angleRad)) return null;
  const deg = angleRad * RAD_TO_DEG;
  if (deg <= EDGE_ALIGN_GOOD_DEG) return EDGE_QUALITY.good;
  if (deg <= EDGE_ALIGN_OK_DEG) return EDGE_QUALITY.ok;
  return EDGE_QUALITY.flat;
}

function finite3(v: THREE.Vector3): boolean {
  return isFinite(v.x) && isFinite(v.y) && isFinite(v.z);
}
